import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import React, { FC, useEffect } from 'react';
import styles from './AppContactDialog.module.css';
import { Close, TryRounded } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { getAuth } from 'firebase/auth';
import { UserData } from '../../../common/authentication/models/user';
import { dutchPhoneNumber } from '../../../common/firebase/regex';
import { ContactPerson } from '../../../environment';
import { FirestoreService } from '../../../common/firebase/firestore.service';
import { handleError } from '../../../common/firebase/auth-error.service';
import { ServiceManager } from '../../../services/service-manager';
import { severity } from '../../../common/models/severity.enum';
import { environment } from '../../../environment.enum';


interface formData {
    email: string, phone: string, company: string, message: string, publicName: string
}

export interface Mail {
    to: string[];
    userId: string;
    template: {
        name: emailTemplate,
        data: {
            email: string;
            phone: string;
            company: string;
            message: string;
            publicName: string;
        }
    };
    env: environment;
}

interface AppContactDialogProps {
    open: boolean,
    setOpen: (open: boolean) => void,
    user: UserData,
    contactPerson: ContactPerson,
}

export enum emailTemplate {
    appContact = 'app-contact',
    appContactToUser = 'app-contact-to-user',
}

const ProjectDialog: FC<AppContactDialogProps> = ({ open, setOpen, user, contactPerson }) => {

    const { register, handleSubmit, formState: { errors }, reset } = useForm<formData>();

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (open) {
            if (user && user.private) {
                const res = { email: user.private.email, company: user.private.company, phone: user.private.phone, publicName: user.public.publicName };
                reset(res);
            }
        }
    }, [open])

    const onSubmit = async (data: formData) => {
        const auth = getAuth();
        const res: Mail = {
            to: [contactPerson.email],
            userId: auth.currentUser.uid,
            template: {
                name: emailTemplate.appContact,
                data: {
                    email: data.email,
                    publicName: data.publicName,
                    phone: data.phone,
                    company: data.company,
                    message: data.message,
                }
            },
            env: process.env.REACT_APP_ENVIRONMENT as environment
        }

        const promiseAppContact = FirestoreService.addDoc('mail', res, (err) => {
            handleError(err);
        });

        res.template.name = emailTemplate.appContactToUser;
        res.to = [data.email];

        const promiseAppContactUser = FirestoreService.addDoc('mail', res, (err) => {
            handleError(err);
        });

        Promise.all([promiseAppContact, promiseAppContactUser]).then((res) => {
            if (res[0] && res[1]) {
                setOpen(false);
                ServiceManager.snackbarService.openSnackbar('Uw bericht was verstuurd!', severity.success);
            }
        })
    }

    return (<div>
        <Dialog fullWidth={true} maxWidth="xs" open={open} onClose={handleClose}>
            <DialogTitle>
                <IconButton onClick={handleClose} className={styles.close} color="secondary" data-testid="close-dialog" aria-label="Dialog dicht" component="span">
                    <Close />
                </IconButton>
                <span className={styles.title}>Volgende Stap</span>
            </DialogTitle>
            <DialogContent>
                <form className={styles.contactForm} data-testid="app-contact-form" onSubmit={handleSubmit(onSubmit)}>
                    <p>
                        Is uw interesse voor MOOS gewekt of past ons bouwsysteem bij uw project? Of heeft u vragen over de configurator?
                    </p>
                    <p className={styles.bold}>
                        Graag nemen we contact met u op om uw project een stap verder te brengen.
                    </p>
                    <div>
                        <TextField id="phone"
                            fullWidth
                            variant="outlined"
                            className="form-input"
                            label="Telefoonnummer (optioneel)"
                            {...register('phone', {
                                pattern: {
                                    value: dutchPhoneNumber,
                                    message: 'Uw telefoonnummer voldoet niet aan de regels.'
                                },
                                maxLength: {
                                    value: 255,
                                    message: 'Uw telefoonnummer is te lang.'
                                },
                            })}
                        />
                        {errors.phone && <p className="input-error">{errors.phone.message}</p>}
                    </div>
                    <div>
                        <TextField id="message"
                            fullWidth
                            variant="outlined"
                            multiline={true}
                            rows={6}
                            className="form-input"
                            label="Bericht"
                            {...register('message', { required: { value: true, message: 'Bericht veld moet ingevuld zijn.' }, maxLength: { value: 1000, message: 'Uw bericht is meer dan 1000 karakters lang.' } })}
                        />
                        {errors.message && <p className="input-error">{errors.message.message}</p>}
                    </div>
                </form>
            </DialogContent>
            <DialogActions>
                <div className={styles.next}>
                    <div >
                        <Button color="primary" data-testid="project-name-submit" size="small" variant="contained" onClick={handleSubmit((d) => onSubmit(d))} >Verstuur</Button>
                    </div>
                    <div className={styles.contactPerson}>
                        {contactPerson &&
                            <div>
                                <div className={styles.title}>Wilt u meteen contact met iemand van MOOS?</div>
                                {contactPerson.photoUrl &&
                                    <>
                                        <img className={styles.contactPersonPhoto} src={contactPerson.photoUrl}></img>
                                        <br></br>
                                    </>
                                }
                                <div className={styles.title}>
                                    {contactPerson.name} {contactPerson.surname}
                                </div>
                                <div>
                                    {contactPerson.title}
                                </div>
                                <div>
                                    <a href={'tel:' + contactPerson.phone} target="_blank" rel="noreferrer">
                                        {contactPerson.phone}
                                    </a>
                                </div>
                                <div>
                                    <a href={'mailto:' + contactPerson.email} target="_blank" rel="noreferrer">
                                        {contactPerson.email}
                                    </a>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </DialogActions>

        </Dialog>
    </div>
    )
};

export default ProjectDialog;
